import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useFocusTrap } from '../../lib/use-focus-trap';
import { MobileMenuNavGroups } from './MobileMenuNavGroups';
import { DrawerHeader, DrawerDownloadLinks } from './MobileMenuDrawerChrome';

/**
 * Mobile menu drawer. Opened by the Topbar's hamburger button, which
 * is shown below the desktop-nav floor of 1136px (2026-08-18,
 * tech-lead decision — the desktop `CewSiteNav` is shown ONLY where
 * its full row fits on a single line; see Topbar's own doc note).
 * That includes CEW's own <=1000px mobile breakpoint AND the
 * 1001-1135px hybrid band, where the bar/logo already use CEW's
 * desktop-matched geometry but the nav cluster itself doesn't fit.
 * Slides in from the RIGHT when tapped (CR-028: mirrors CEW's own
 * off-canvas menu, which is slide-out-from-right — this replaces the
 * tool's previous slide-from-left geometry). Contains the tool's 6
 * page tabs, a replica "cew.georgetown.edu" nav group (CewSiteNav's
 * items, since the desktop nav is hidden below 1136px), and the
 * PDF download links (report + press release, CR-034/035).
 *
 * Dismiss paths:
 *   - Tap the backdrop
 *   - Press Escape
 *   - Tap any nav item (the new page closes the drawer via the
 *     location-change effect)
 *   - Tap the X close button in the drawer header
 *
 * Renders nothing when `isOpen` is false. When open the panel is
 * pinned at z-[1100] — above Leaflet's internal panes (max ~1000)
 * and documented as the MOBILE_DRAWER tier in CLAUDE.md z-stack
 * convention. Leaflet's vector panes can reach z-1000, so the
 * mobile drawer is the only surface that lives above the
 * 300-400 ladder.
 *
 * A11y contract (mirrors RowPeekDrawer):
 *   - role="dialog", aria-modal="true"
 *   - id="mobile-menu-drawer" matches Topbar's `aria-controls`
 *   - Focus moves to the close button on open (rAF defers until the
 *     panel exists in the DOM — the render-state machine mounts the
 *     panel synchronously on `entering`, so the close button is in
 *     the DOM by the time rAF fires; the slide animation visually
 *     follows but doesn't gate the focus move)
 *   - Focus restores to the element that opened the drawer on close
 *   - Focus trapped within the drawer while open (Tab / Shift+Tab cycle)
 *   - Slide animation respects `motion-reduce`
 */

interface MobileMenuDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

// Slide animation duration. Must match the CSS transition duration
// below (`duration-300`). When the drawer closes we need to keep the
// markup mounted long enough for the slide-out to finish before
// unmounting — otherwise the panel just disappears instantly.
const ANIMATION_MS = 300;

type RenderState = 'unmounted' | 'entering' | 'open' | 'exiting';

export function MobileMenuDrawer({ isOpen, onClose }: MobileMenuDrawerProps) {
  const location = useLocation();
  // Animation state machine. Lifecycle:
  //   isOpen=true  → unmounted → entering (rAF) → open
  //   isOpen=false → exiting → (ANIMATION_MS later) → unmounted
  const [renderState, setRenderState] = useState<RenderState>('unmounted');

  // Refs for focus management. `panelRef` is what useFocusTrap binds
  // to; `closeButtonRef` is the initial focus target on open;
  // `triggerElementRef` remembers the hamburger so focus can be
  // restored when the drawer closes (mirrors RowPeekDrawer:55-63).
  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Mount the panel off-screen on the RIGHT (`entering` →
      // `translate-x-full`), then flip to `open` so the CSS
      // transition to `translate-x-0` plays (CR-028: mirrors CEW's
      // own off-canvas menu, which slides in from the right).
      //
      // Double rAF: a single rAF fires before the next paint, which
      // is too early — React can batch the `entering` and `open`
      // state updates into a single committed render and the browser
      // never paints `translate-x-full` as a starting frame, so the
      // transition skips and the drawer pops in instantly. Two rAFs
      // guarantees the `entering` paint commits first, giving the
      // transition both endpoints to animate between.
      setRenderState('entering');
      let id2: number | null = null;
      const id1 = window.requestAnimationFrame(() => {
        id2 = window.requestAnimationFrame(() => setRenderState('open'));
      });
      return () => {
        window.cancelAnimationFrame(id1);
        if (id2 !== null) window.cancelAnimationFrame(id2);
      };
    }
    // Closing path: only run the slide-out if we were previously
    // mounted. If renderState is already 'unmounted', do nothing.
    if (renderState !== 'unmounted') {
      setRenderState('exiting');
      const timer = window.setTimeout(
        () => setRenderState('unmounted'),
        ANIMATION_MS,
      );
      return () => window.clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Capture the trigger element synchronously when `isOpen` flips to
  // true so it's locked in before render-state moves through
  // entering → open. Restore focus to the trigger when `isOpen` flips
  // to false (don't wait for the slide-out to finish — the user has
  // already moved on mentally).
  useEffect(() => {
    if (isOpen) {
      triggerElementRef.current = (document.activeElement as HTMLElement | null) ?? null;
      return undefined;
    }
    const trigger = triggerElementRef.current;
    if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
      trigger.focus();
    }
    triggerElementRef.current = null;
    return undefined;
  }, [isOpen]);

  // Move focus to the close button once the panel is actually in the
  // DOM. The render-state machine gates this on renderState rather
  // than isOpen — when isOpen flips to true the first commit still
  // has renderState='unmounted' (the entering setState happens in an
  // effect AFTER the initial paint), so a focus call keyed on isOpen
  // would fire before closeButtonRef.current populates and no-op.
  // Gating on renderState='entering' guarantees the panel has
  // mounted and the ref is live. rAF further defers to the next
  // frame so the focus call doesn't race the same paint that mounts
  // the close button (some browsers fail focus() on elements
  // mounted in the same tick).
  useEffect(() => {
    if (renderState !== 'entering' && renderState !== 'open') return;
    const handle = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(handle);
  }, [renderState]);

  // Trap Tab/Shift+Tab inside the drawer while it's open. Gated on
  // the render state machine — focus trap activates once we're past
  // 'unmounted' and the panel is in the DOM.
  useFocusTrap(panelRef, renderState === 'open' || renderState === 'entering');

  // Escape closes the drawer.
  useEffect(() => {
    if (renderState === 'unmounted') return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [renderState, onClose]);

  // Auto-close when the route changes (e.g., user taps a nav item).
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock body scroll while the drawer is visible. Released as soon
  // as it starts exiting so the page underneath isn't frozen during
  // the slide-out animation.
  useEffect(() => {
    if (renderState !== 'open' && renderState !== 'entering') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [renderState]);

  if (renderState === 'unmounted') return null;

  // While entering/exiting the panel is off-screen on the RIGHT
  // (`translate-x-full`); the `open` state pulls it to its resting
  // position (`translate-x-0`). The backdrop fades in/out in parallel.
  const isVisible = renderState === 'open';

  return (
    <div
      id="mobile-menu-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Page menu"
      // z-[1100] sits above Leaflet's internal panes (max ~1000)
      // and is documented as the MOBILE_DRAWER tier in CLAUDE.md
      // z-stack convention. Mobile drawer goes higher than the
      // 300-400 ladder because Leaflet's vector panes can reach
      // z-1000; everything else stays in the 300-400 range.
      className="fixed inset-0 z-[1100]"
    >
      {/* Backdrop — click to dismiss. Opacity transition matches the
          panel's translate transition for a coherent feel. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-navy/40 backdrop-blur-[2px] transition-opacity duration-300 ease-out motion-reduce:transition-none',
          isVisible ? 'opacity-100' : 'opacity-0',
        )}
      />

      {/* Drawer panel — slides in from the RIGHT (CR-028: mirrors
          CEW's own off-canvas menu geometry). 280px wide is wide
          enough to comfortably hold the longest label ("PROVIDERS")
          and tap targets without dominating the viewport. */}
      <aside
        ref={panelRef}
        className={cn(
          'absolute bottom-0 right-0 top-0 flex w-drawer-mobile flex-col bg-white shadow-drawer',
          'transition-transform duration-300 ease-out motion-reduce:transition-none',
          isVisible ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <DrawerHeader closeButtonRef={closeButtonRef} onClose={onClose} />

        <div className="flex-1 overflow-y-auto">
          <MobileMenuNavGroups onClose={onClose} />
        </div>

        <DrawerDownloadLinks onClose={onClose} />
      </aside>
    </div>
  );
}
